/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { ContentStackFile, ContentStackImage, SizePerDevice } from '../interface';
import { type ImageSize } from '../interface/models';

type Props = {
  image?:
    | ContentStackImage
    | {
        image_alt_text: NonNullable<ContentStackImage['image_alt_text']>;

        image_url: ContentStackFile['url'];

        attributes: undefined;
      };
  sizePerDevice: SizePerDevice;
};

type Image = {
  src: string;
  width: ImageSize['width'];
  height: ImageSize['height'];
  transformation?: string;
};
type DeviceType = 'desktop' | 'tablet' | 'mobile';
const CropWidths = {
  desktop: 1560,
  tablet: 1139,
  mobile: 719,
};

const useResponsiveImageData = ({ image, sizePerDevice }: Props) => {
  const imgSrcs = image?.attributes?.reduce<
    Record<
      string,
      Pick<Image, 'src' | 'transformation'> & { crop_width?: number; crop_height?: number }
    >
  >((acc, { device, device_image, transformations }) => {
    const deviceType = (device?.toLowerCase() ?? 'desktop') as DeviceType;
    let deviceImage = device_image;
    const hasRegionOfInterest =
      !!transformations?.transformation_type &&
      transformations?.xfocuspoint != null &&
      transformations?.yfocuspoint != null;
    if (hasRegionOfInterest) {
      const separator = deviceImage.includes('?') ? '&' : '?';
      deviceImage = `${deviceImage}${separator}im=RegionOfInterestCrop,width=${transformations?.crop_width ?? CropWidths[deviceType]},height=${transformations.crop_height ?? 400},regionOfInterest=(${transformations.xfocuspoint},${transformations.yfocuspoint})`;
    }
    return {
      ...acc,
      [deviceType]: {
        src: deviceImage ?? '',
        transformation: hasRegionOfInterest ? transformations?.transformation_type : '',
        crop_width: transformations?.crop_width,
        crop_height: transformations?.crop_height,
      },
    };
  }, {});

  return Object.keys(sizePerDevice).reduce<Record<string, Image>>((acc, key) => {
    const src = imgSrcs?.[key]?.src ?? image?.image_url ?? '';
    const transformation = imgSrcs?.[key]?.transformation ?? '';
    const width = imgSrcs?.[key]?.crop_width ?? sizePerDevice[key].width;
    const height = imgSrcs?.[key]?.crop_height ?? sizePerDevice[key].height;
    return { ...acc, [key]: { width, height, src, transformation } };
  }, {});
};

export { useResponsiveImageData };
